import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, UserMinusIcon, AlertTriangleIcon, CheckIcon, XIcon, Loader2Icon, ShieldIcon, DatabaseIcon, UsersIcon, SettingsIcon, ArrowRightIcon, FileTextIcon, BriefcaseIcon } from 'lucide-react';
interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  title: string;
  profile: string;
  role: string;
  status: string;
  subordinates: string[];
  hasNMLS: boolean;
}
const MOCK_USERS: User[] = [{
  id: '1',
  firstName: 'John',
  lastName: 'Martinez',
  email: 'john.martinez@rapidhomelending.com',
  title: 'Loan Officer',
  profile: 'Loan Associate',
  role: 'Jr. Loan Officer',
  status: 'Active',
  subordinates: [],
  hasNMLS: true
}, {
  id: '2',
  firstName: 'Sarah',
  lastName: 'Chen',
  email: 'sarah.chen@rapidhomelending.com',
  title: 'Jr Processor',
  profile: 'Loan Processor',
  role: 'Processor',
  status: 'Active',
  subordinates: [],
  hasNMLS: false
}, {
  id: '3',
  firstName: 'Mike',
  lastName: 'Johnson',
  email: 'mike.johnson@rapidhomelending.com',
  title: 'Team Lead',
  profile: 'Team Lead',
  role: 'Team Lead',
  status: 'Active',
  subordinates: ['1', '5'],
  hasNMLS: true
}, {
  id: '4',
  firstName: 'Emily',
  lastName: 'Davis',
  email: 'emily.davis@rapidhomelending.com',
  title: 'Division Lead',
  profile: 'Division Lead',
  role: 'Division Lead',
  status: 'Active',
  subordinates: ['3', '7'],
  hasNMLS: true
}, {
  id: '5',
  firstName: 'David',
  lastName: 'Wilson',
  email: 'david.wilson@rapidhomelending.com',
  title: 'Loan Associate',
  profile: 'Loan Associate',
  role: 'Solution Desk',
  status: 'Active',
  subordinates: [],
  hasNMLS: false
}, {
  id: '6',
  firstName: 'Lisa',
  lastName: 'Thompson',
  email: 'lisa.thompson@rapidhomelending.com',
  title: 'Sr Processor',
  profile: 'Processing Director',
  role: 'Processor Manager',
  status: 'Active',
  subordinates: ['2', '8'],
  hasNMLS: false
}, {
  id: '7',
  firstName: 'James',
  lastName: 'Brown',
  email: 'james.brown@rapidhomelending.com',
  title: 'Team Lead',
  profile: 'Team Lead',
  role: 'Team Lead',
  status: 'Active',
  subordinates: ['9', '10'],
  hasNMLS: true
}, {
  id: '8',
  firstName: 'Amanda',
  lastName: 'Garcia',
  email: 'amanda.garcia@rapidhomelending.com',
  title: 'Doc Specialist',
  profile: 'Loan Processor',
  role: 'Processor',
  status: 'Active',
  subordinates: [],
  hasNMLS: false
}, {
  id: '9',
  firstName: 'Robert',
  lastName: 'Taylor',
  email: 'robert.taylor@rapidhomelending.com',
  title: 'Loan Officer',
  profile: 'Loan Associate',
  role: 'Jr. Loan Officer',
  status: 'Active',
  subordinates: [],
  hasNMLS: true
}, {
  id: '10',
  firstName: 'Jennifer',
  lastName: 'Lee',
  email: 'jennifer.lee@rapidhomelending.com',
  title: 'Loan Officer',
  profile: 'Loan Associate',
  role: 'Jr. Loan Officer',
  status: 'Active',
  subordinates: [],
  hasNMLS: true
}];
interface OffboardingConfig {
  title: string;
  removePermissions: string[];
  revokeLicenses: string[];
  removePublicGroups: string[];
  requiresRoundRobinDeactivation: boolean;
  requiresOwnershipTransfer: boolean;
  createRoundRobinOnExit: boolean;
  requiresNMLSHandling: boolean;
}
const MOCK_METADATA: Record<string, OffboardingConfig> = {
  'Loan Officer': {
    title: 'Loan Officer',
    removePermissions: ['Sales User', 'Encompass Sync', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse', 'Dialer'],
    removePublicGroups: ['All Sales', 'West Coast Sales'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: true
  },
  'Jr Processor': {
    title: 'Jr Processor',
    removePermissions: ['Processor Base', 'DocuSign Sender'],
    revokeLicenses: ['Salesforce', 'Pulse'],
    removePublicGroups: ['All Processors'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: true,
    requiresNMLSHandling: false
  },
  'Sr Processor': {
    title: 'Sr Processor',
    removePermissions: ['Processor Admin', 'DocuSign Sender', 'Encompass Admin'],
    revokeLicenses: ['Salesforce', 'Pulse'],
    removePublicGroups: ['All Processors', 'Processor Managers'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: false
  },
  'Team Lead': {
    title: 'Team Lead',
    removePermissions: ['Sales Manager', 'Reporting Admin', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse', 'Dialer'],
    removePublicGroups: ['All Sales', 'Team Leads'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: true
  },
  'Division Lead': {
    title: 'Division Lead',
    removePermissions: ['Division Admin', 'Reporting Admin', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse', 'Dialer'],
    removePublicGroups: ['All Sales', 'Division Leads', 'Executive'],
    requiresRoundRobinDeactivation: false,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: true
  },
  'Doc Specialist': {
    title: 'Doc Specialist',
    removePermissions: ['Doc Specialist Base'],
    revokeLicenses: ['Salesforce'],
    removePublicGroups: ['All Processors'],
    requiresRoundRobinDeactivation: false,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: true,
    requiresNMLSHandling: false
  },
  'Loan Associate': {
    title: 'Loan Associate',
    removePermissions: ['Sales User', 'Dialer Access'],
    revokeLicenses: ['Salesforce', 'Pulse'],
    removePublicGroups: ['All Sales'],
    requiresRoundRobinDeactivation: true,
    requiresOwnershipTransfer: true,
    createRoundRobinOnExit: false,
    requiresNMLSHandling: false
  }
};
const DEFAULT_CONFIG: OffboardingConfig = {
  title: 'Default',
  removePermissions: ['Standard User'],
  revokeLicenses: ['Salesforce'],
  removePublicGroups: ['All Users'],
  requiresRoundRobinDeactivation: false,
  requiresOwnershipTransfer: true,
  createRoundRobinOnExit: false,
  requiresNMLSHandling: false
};
const AVATAR_COLORS = ['bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700', 'bg-amber-100 text-amber-700', 'bg-pink-100 text-pink-700', 'bg-indigo-100 text-indigo-700'];
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
interface OffboardingPanelProps {
  onStepChange?: (step: number) => void;
}
export function OffboardingPanel({
  onStepChange
}: OffboardingPanelProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [reassignments, setReassignments] = useState<Record<string, string>>({});
  const [transferLeads, setTransferLeads] = useState<string>('');
  const [transferLoans, setTransferLoans] = useState<string>('');
  const [transferCases, setTransferCases] = useState<string>('');
  const [separationReason, setSeparationReason] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [deactivatedUsers, setDeactivatedUsers] = useState<Set<string>>(new Set());
  useEffect(() => {
    onStepChange?.(currentStep);
  }, [currentStep, onStepChange]);
  const activeUsers = useMemo(() => {
    return MOCK_USERS.filter(u => !deactivatedUsers.has(u.id));
  }, [deactivatedUsers]);
  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return activeUsers.filter(user => user.firstName.toLowerCase().includes(query) || user.lastName.toLowerCase().includes(query) || user.email.toLowerCase().includes(query) || user.title.toLowerCase().includes(query));
  }, [searchQuery, activeUsers]);
  const selectedUser = useMemo(() => {
    return activeUsers.find(u => u.id === selectedUserId) || null;
  }, [selectedUserId, activeUsers]);
  const config = useMemo(() => {
    if (!selectedUser) return null;
    return MOCK_METADATA[selectedUser.title] || DEFAULT_CONFIG;
  }, [selectedUser]);
  const subordinates = useMemo(() => {
    if (!selectedUser) return [];
    return activeUsers.filter(u => selectedUser.subordinates.includes(u.id));
  }, [selectedUser, activeUsers]);
  const handleNext = () => {
    if (currentStep === 1 && subordinates.length === 0) {
      // Skip reassignment if no subordinates
      setCurrentStep(3);
    } else if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep === 3 && subordinates.length === 0) {
      setCurrentStep(1);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const executeOffboarding = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      if (selectedUserId) {
        setDeactivatedUsers(prev => new Set(prev).add(selectedUserId));
      }
      setCurrentStep(4);
    }, 2000);
  };
  const resetFlow = () => {
    setSelectedUserId(null);
    setSearchQuery('');
    setReassignments({});
    setTransferLeads('');
    setTransferLoans('');
    setTransferCases('');
    setSeparationReason('');
    setCurrentStep(0);
  };
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        // Select User
        return <div className="flex flex-col h-full" data-id="element-15">
            <div className="mb-6" data-id="element-16">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-17">
                Identify User
              </h2>
              <p className="text-sm text-gray-500" data-id="element-18">
                Search and select the user to terminate. This will initiate the
                metadata-driven offboarding process.
              </p>
            </div>

            <div className="relative mb-6" data-id="element-19">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-21" />
              </div>
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] sm:text-sm transition-colors" placeholder="Search users by name, email, or title..." data-id="element-22" />
            </div>

            <div className="flex-1 overflow-y-auto pb-6" data-id="element-23">
              {filteredUsers.length === 0 ? <div className="text-center py-12" data-id="element-24">
                  <p className="text-sm text-gray-500" data-id="element-25">
                    No active users found matching "{searchQuery}"
                  </p>
                </div> : <div className="space-y-2" data-id="element-26">
                  {filteredUsers.map(user => {
                const isSelected = selectedUserId === user.id;
                return <div key={user.id} onClick={() => setSelectedUserId(user.id)} className={`flex items-center p-4 rounded-xl border cursor-pointer transition-all ${isSelected ? 'border-[#2563EB] bg-blue-50 shadow-md ring-1 ring-[#2563EB]/20' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 shadow-sm'}`} data-id="element-27">
                        <div className="flex-shrink-0 mr-4" data-id="element-28">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${getAvatarColor(user.firstName)}`} data-id="element-29">
                            {user.firstName[0]}
                            {user.lastName[0]}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-30">
                          <div className="flex items-center justify-between mb-1" data-id="element-31">
                            <p className="text-base font-bold text-gray-900 truncate" data-id="element-32">
                              {user.firstName} {user.lastName}
                            </p>
                            {user.subordinates.length > 0 && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800" data-id="element-33">
                                <UsersIcon className="w-3 h-3 mr-1" data-id="element-34" />
                                Manages {user.subordinates.length}
                              </span>}
                          </div>
                          <div className="flex items-center text-sm text-gray-500" data-id="element-35">
                            <span className="font-medium text-gray-700 mr-3" data-id="element-36">
                              {user.title}
                            </span>
                            <span className="truncate" data-id="element-37">{user.email}</span>
                          </div>
                        </div>
                      </div>;
              })}
                </div>}
            </div>
          </div>;
      case 1:
        // Review Config
        if (!selectedUser || !config) return null;
        return <div className="flex flex-col h-full" data-id="element-38">
            <div className="mb-6" data-id="element-39">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-40">
                Review Configuration
              </h2>
              <p className="text-sm text-gray-500" data-id="element-41">
                Loaded{' '}
                <code className="bg-gray-100 px-1 py-0.5 rounded text-xs" data-id="element-42">
                  User_Offboarding_Config__mdt
                </code>{' '}
                for title:{' '}
                <span className="font-bold text-gray-700" data-id="element-43">
                  {selectedUser.title}
                </span>
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden mb-6" data-id="element-44">
              <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center" data-id="element-45">
                <SettingsIcon className="w-5 h-5 text-gray-500 mr-2" data-id="element-46" />
                <h3 className="text-sm font-bold text-gray-900" data-id="element-47">
                  Metadata Resolution
                </h3>
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-48">
                <div data-id="element-49">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-50">
                    Permissions to Remove
                  </h4>
                  <ul className="space-y-2" data-id="element-51">
                    {config.removePermissions.map(p => <li key={p} className="flex items-center text-sm text-gray-700" data-id="element-52">
                        <XIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-53" /> {p}
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-54">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-55">
                    Licenses to Revoke
                  </h4>
                  <ul className="space-y-2" data-id="element-56">
                    {config.revokeLicenses.map(l => <li key={l} className="flex items-center text-sm text-gray-700" data-id="element-57">
                        <XIcon className="w-4 h-4 text-red-500 mr-2" data-id="element-58" /> {l}
                      </li>)}
                  </ul>
                </div>
              </div>
              <div className="p-4 border-t border-gray-100 bg-gray-50 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-59">
                <div className="flex items-center justify-between" data-id="element-60">
                  <span className="text-sm text-gray-600" data-id="element-61">
                    Round Robin Deactivation
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${config.requiresRoundRobinDeactivation ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`} data-id="element-62">
                    {config.requiresRoundRobinDeactivation ? 'Required' : 'Skip'}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-63">
                  <span className="text-sm text-gray-600" data-id="element-64">
                    Ownership Transfer
                  </span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${config.requiresOwnershipTransfer ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`} data-id="element-65">
                    {config.requiresOwnershipTransfer ? 'Required' : 'Skip'}
                  </span>
                </div>
              </div>
            </div>

            {/* Special Logic Alerts */}
            <div className="space-y-3" data-id="element-66">
              {config.createRoundRobinOnExit && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start" data-id="element-67">
                  <AlertTriangleIcon className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-68" />
                  <div data-id="element-69">
                    <h4 className="text-sm font-bold text-blue-900" data-id="element-70">
                      Processor Special Logic
                    </h4>
                    <p className="text-xs text-blue-700 mt-1" data-id="element-71">
                      User is a Processor (Not Director). System will
                      automatically create a Round Robin entry upon deactivation
                      to redistribute active files.
                    </p>
                  </div>
                </div>}
              {config.requiresNMLSHandling && selectedUser.hasNMLS && <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start" data-id="element-72">
                  <ShieldIcon className="w-5 h-5 text-purple-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-73" />
                  <div data-id="element-74">
                    <h4 className="text-sm font-bold text-purple-900" data-id="element-75">
                      NMLS State License Handling
                    </h4>
                    <p className="text-xs text-purple-700 mt-1" data-id="element-76">
                      User has active NMLS requirements. State license
                      assignments will be archived and removed from active
                      routing pools.
                    </p>
                  </div>
                </div>}
            </div>
          </div>;
      case 2:
        // Reassignment
        if (!selectedUser) return null;
        return <div className="flex flex-col h-full" data-id="element-77">
            <div className="mb-6" data-id="element-78">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-79">
                Reassign Subordinates
              </h2>
              <p className="text-sm text-gray-500" data-id="element-80">
                <span className="font-bold text-gray-700" data-id="element-81">
                  {selectedUser.firstName} {selectedUser.lastName}
                </span>{' '}
                manages {subordinates.length} users. You must reassign them
                before termination.
              </p>
            </div>

            <div className="space-y-4" data-id="element-82">
              {subordinates.map(sub => <div key={sub.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-83">
                  <div className="flex items-center" data-id="element-84">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold mr-3 ${getAvatarColor(sub.firstName)}`} data-id="element-85">
                      {sub.firstName[0]}
                      {sub.lastName[0]}
                    </div>
                    <div data-id="element-86">
                      <p className="text-sm font-bold text-gray-900" data-id="element-87">
                        {sub.firstName} {sub.lastName}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-88">{sub.title}</p>
                    </div>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-gray-300 hidden md:block" data-id="element-89" />
                  <div className="flex-1 max-w-xs" data-id="element-90">
                    <select value={reassignments[sub.id] || ''} onChange={e => setReassignments({
                  ...reassignments,
                  [sub.id]: e.target.value
                })} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-[#2563EB] focus:border-[#2563EB] rounded-md" data-id="element-91">
                      <option value="" data-id="element-92">Select new manager...</option>
                      {activeUsers.filter(u => u.id !== selectedUser.id && u.id !== sub.id && (u.title.includes('Lead') || u.title.includes('Manager') || u.title.includes('Director'))).map(manager => <option key={manager.id} value={manager.id} data-id="element-93">
                            {manager.firstName} {manager.lastName} (
                            {manager.title})
                          </option>)}
                    </select>
                  </div>
                </div>)}
            </div>
          </div>;
      case 3:
        // Deactivation Actions
        if (!selectedUser || !config) return null;
        return <div className="flex flex-col h-full" data-id="element-94">
            <div className="mb-6" data-id="element-95">
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-96">
                Deactivation Actions
              </h2>
              <p className="text-sm text-gray-500" data-id="element-97">
                Review the final actions to be executed. Ownership transfer is
                required.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6" data-id="element-98">
              <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2" data-id="element-99">
                Ownership Transfer
              </h3>
              <p className="text-xs text-gray-500 mb-5" data-id="element-100">
                Assign each record type to a specific user or distribute via
                Round Robin.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-101">
                {[{
                label: 'Leads',
                value: transferLeads,
                setter: setTransferLeads
              }, {
                label: 'Loans',
                value: transferLoans,
                setter: setTransferLoans
              }, {
                label: 'Cases',
                value: transferCases,
                setter: setTransferCases
              }].map(({
                label,
                value,
                setter
              }) => <div key={label} data-id="element-102">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-103">
                      Transfer {label} to
                    </label>
                    <select value={value} onChange={e => setter(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-[#2563EB] focus:border-[#2563EB] rounded-md" data-id="element-104">
                      <option value="" data-id="element-105">Select target...</option>
                      <option value="round-robin" data-id="element-106">🔄 Via Round Robin</option>
                      {activeUsers.filter(u => u.id !== selectedUser.id).map(u => <option key={u.id} value={u.id} data-id="element-107">
                            {u.firstName} {u.lastName} ({u.title})
                          </option>)}
                    </select>
                  </div>)}
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6" data-id="element-108">
              <h3 className="text-sm font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2" data-id="element-109">
                Reason for Separation
              </h3>
              <div className="max-w-2xl" data-id="element-110">
                <select value={separationReason} onChange={e => setSeparationReason(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-[#2563EB] focus:border-[#2563EB] rounded-md mb-3" data-id="element-111">
                  <option value="" data-id="element-112">Select reason...</option>
                  <option value="voluntary-resignation" data-id="element-113">
                    Voluntary Resignation
                  </option>
                  <option value="involuntary-termination" data-id="element-114">
                    Involuntary Termination
                  </option>
                  <option value="performance" data-id="element-115">Performance Issues</option>
                  <option value="policy-violation" data-id="element-116">Policy Violation</option>
                  <option value="position-elimination" data-id="element-117">
                    Position Elimination
                  </option>
                  <option value="retirement" data-id="element-118">Retirement</option>
                  <option value="end-of-contract" data-id="element-119">End of Contract</option>
                  <option value="other" data-id="element-120">Other</option>
                </select>
                <p className="text-xs text-gray-500" data-id="element-121">
                  This will be recorded in the user's employment history and
                  audit log.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6" data-id="element-122">
              <div className="flex items-center mb-4" data-id="element-123">
                <AlertTriangleIcon className="w-6 h-6 text-amber-600 mr-2" data-id="element-124" />
                <h3 className="text-base font-bold text-amber-900" data-id="element-125">
                  Execution Summary
                </h3>
              </div>
              <ul className="space-y-3 text-sm text-amber-800" data-id="element-126">
                <li className="flex items-start" data-id="element-127">
                  <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-128" />
                  <span data-id="element-129">
                    Freeze user login and deactivate Salesforce account
                  </span>
                </li>
                <li className="flex items-start" data-id="element-130">
                  <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-131" />
                  <span data-id="element-132">
                    Remove {config.removePermissions.length} permission sets and{' '}
                    {config.revokeLicenses.length} package licenses
                  </span>
                </li>
                {config.requiresRoundRobinDeactivation && <li className="flex items-start" data-id="element-133">
                    <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-134" />
                    <span data-id="element-135">
                      Remove from active Round Robin distribution pools
                    </span>
                  </li>}
                {subordinates.length > 0 && <li className="flex items-start" data-id="element-136">
                    <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-137" />
                    <span data-id="element-138">
                      Reassign {subordinates.length} subordinates to new
                      managers
                    </span>
                  </li>}
                {config.createRoundRobinOnExit && <li className="flex items-start" data-id="element-139">
                    <CheckIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-140" />
                    <span className="font-bold" data-id="element-141">
                      Execute Processor Special Logic: Create Round Robin entry
                      for active files
                    </span>
                  </li>}
              </ul>
            </div>
          </div>;
      case 4:
        // Execute
        return <div className="flex-1 flex flex-col items-center justify-center p-10 text-center min-h-[500px]" data-id="element-142">
            {isProcessing ? <div className="flex flex-col items-center" data-id="element-143">
                <Loader2Icon className="w-16 h-16 text-[#2563EB] animate-spin mb-6" data-id="element-144" />
                <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-145">
                  Executing Offboarding...
                </h2>
                <p className="text-sm text-gray-500" data-id="element-146">
                  Processing metadata rules and transferring records.
                </p>
              </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="flex flex-col items-center" data-id="element-147">
                <div className="w-20 h-20 bg-[#2E844A] rounded-full flex items-center justify-center shadow-lg mb-6" data-id="element-148">
                  <CheckIcon className="w-10 h-10 text-white" data-id="element-149" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-150">
                  Offboarding Complete
                </h2>
                <p className="text-sm text-gray-600 mb-8 max-w-md" data-id="element-151">
                  User has been successfully deactivated according to the{' '}
                  <span className="font-semibold" data-id="element-152">{config?.title}</span>{' '}
                  metadata configuration.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-10 w-full max-w-lg text-left" data-id="element-153">
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-154">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-155">
                      {config?.removePermissions.length || 0}
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-156">
                      Permissions Removed
                    </div>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-157">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-158">
                      {config?.revokeLicenses.length || 0}
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-159">
                      Licenses Revoked
                    </div>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-160">
                    <div className="text-2xl font-bold text-gray-900" data-id="element-161">
                      {subordinates.length}
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-162">
                      Subordinates Reassigned
                    </div>
                  </div>
                  <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm" data-id="element-163">
                    <div className="text-2xl font-bold text-green-600" data-id="element-164">
                      Success
                    </div>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-165">
                      Ownership Transferred
                    </div>
                  </div>
                </div>

                <button onClick={resetFlow} className="px-6 py-2.5 bg-white border border-gray-300 text-sm text-gray-700 font-medium rounded-full shadow-sm hover:bg-gray-50 transition-colors" data-id="element-166">
                  Start New Offboarding
                </button>
              </motion.div>}
          </div>;
      default:
        return null;
    }
  };
  // Determine if we can advance to the next step
  const canAdvance = useMemo(() => {
    if (currentStep === 0) return selectedUserId !== null;
    if (currentStep === 1) return true;
    if (currentStep === 2) {
      // Must reassign all subordinates
      return subordinates.every(sub => reassignments[sub.id]);
    }
    if (currentStep === 3) return transferLeads !== '' && transferLoans !== '' && transferCases !== '' && separationReason !== '';
    return false;
  }, [currentStep, selectedUserId, subordinates, reassignments, transferLeads, transferLoans, transferCases, separationReason]);
  return <div className="flex flex-col h-full relative" data-id="element-167">
      {/* Header */}
      <div className="p-6 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-168">
        <div className="flex items-center justify-between" data-id="element-169">
          <div data-id="element-170">
            <h1 className="text-xl font-bold text-gray-900" data-id="element-171">
              Offboarding Engine
            </h1>
            <p className="text-sm text-gray-500" data-id="element-172">
              Metadata-driven user termination and reassignment.
            </p>
          </div>
          {selectedUser && currentStep > 0 && currentStep < 4 && <div className="flex items-center bg-gray-50 rounded-full px-4 py-1.5 border border-gray-200" data-id="element-173">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold mr-2 ${getAvatarColor(selectedUser.firstName)}`} data-id="element-174">
                {selectedUser.firstName[0]}
                {selectedUser.lastName[0]}
              </div>
              <span className="text-sm font-medium text-gray-700" data-id="element-175">
                {selectedUser.firstName} {selectedUser.lastName}
              </span>
            </div>}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 pb-24" data-id="element-176">
        <AnimatePresence mode="wait" data-id="element-177">
          <motion.div key={currentStep} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-178">
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer Navigation */}
      {currentStep < 4 && <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 bg-white flex items-center justify-between" data-id="element-179">
          {currentStep > 0 ? <button onClick={handleBack} className="px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors" data-id="element-180">
              Back
            </button> : <div data-id="element-181"></div>}

          {currentStep === 3 ? <button onClick={executeOffboarding} disabled={!canAdvance || isProcessing} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm ${!canAdvance || isProcessing ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`} data-id="element-182">
              Execute Termination
            </button> : <button onClick={handleNext} disabled={!canAdvance} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm ${!canAdvance ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]'}`} data-id="element-183">
              Next Step
            </button>}
        </div>}
    </div>;
}